import * as React from "react";

import "./pending-route.scss";

import {FontAwesomeIcon}    from "@fortawesome/react-fontawesome";
import {
    faArrowDown,
    faArrowUp,
    faCircleXmark,
    faSearch,
}                           from "@fortawesome/free-solid-svg-icons";

import {UseState}           from "@Common/react/use-state";
import {
    ComponentLink,
    componentHelper as component,
    makeChain,
    valueHelper as value,
}                           from "@Common/react/cst2";
import {Select2}            from "@Common/ui-components/select-2/select-2";

import {consumeContext}     from "@Xpresso/loaders/consume-context";
import {
    AGENT_LEVEL,
    BRANCH_LEVEL,
    COMPANY_LEVEL,
}                           from "@Xpresso/routes/common/logic/user-level";

import {OrdersLayout}               from "../common/orders-layout";
import {
    OrdersSubmittedTable,
    OrdersSubmittedState}           from "./orders-submitted-table/orders-submitted-table";
import {PendingOrdersTable}         from "./orders-table/orders-table";
import {PendingOrdersTableState}    from "./orders-table/orders-table-state";

export const AccountPendingRoute = () => makeChain<JSX.Element>()
    .then(...consumeContext("userLevels")) // uses Xpresso wrapper of consumeContext
    .then(...consumeContext("routerHistory"))

    .then(component(OrdersLayout)(
        ({}, OrdersLayout) => OrdersLayout({activeTab: "pending"})
    ))

    .then("searchState", ({}, next:ComponentLink<typeof UseState<string>>) => (
        UseState({next, initValue: ""})
    ))

    .then("searchInputTip", value(
        ({userLevels: {internalUser, userLevel}}) => (
            userLevel === COMPANY_LEVEL ? (
                "Search by Order Name, Order ID, Cart ID, Office Name or User Name"
            ) : userLevel === BRANCH_LEVEL ? (
                "Search by Order Name, Order ID, Cart ID, or User Name"
            ) : (
                "Search by Order Name, Order ID, or Cart ID"
            )
        ) + (internalUser ? " (Internal: idBranch or idAgent)" : "")
    ))

    .then("sortModes", value(
        ({userLevels: {userLevel}}) => [
            {id: "cartId",  label: "Cart"},
            {id: "orderId", label: "Order ID"},
            {id: "date",    label: "Date"},
            ...(userLevel === COMPANY_LEVEL ? [{id: "branchName", label: "Office"}] : []),
            ...(userLevel !== AGENT_LEVEL   ? [{id: "agentName", label: "User"}]   : []),
        ]
    ))

    .then("sortState", component(UseState as typeof UseState<{id: string, ascending: boolean}>)(
        ({}, S) => S({initValue: {id: "date", ascending: false}})
    ))

    .then("pendingOrdersState", component(PendingOrdersTableState)(
        ({
            searchState: {value: searchQuery},
            sortState: {value: sortMode}
        }, S) => S({searchQuery, sortMode})
    ))
    .then("ordersSubmittedState", component(OrdersSubmittedState)(
        ({
            pendingOrdersState : {reload: reloadPendingOrders},
            searchState        : {value: searchQuery},
        }, S) => S({searchQuery, onSubmissionCancelled: reloadPendingOrders})
    ))

    .then("expandedState", component(UseState as typeof UseState<{pending: boolean, submitted: boolean}>)(
        ({}, C) => C({initValue: {pending: true, submitted: true}})
    ))

    .then(({
        expandedState: {
            onChange: setExpandedState,
            value: {pending: pendingExpanded, submitted: submittedExpanded}
        },
        ordersSubmittedState,
        pendingOrdersState,
        searchInputTip,
        searchState: {onChange: setSearch, value: search},
        sortModes,
        sortState,
        sortState: {change: changeSortMode, onChange: setSortMode, value: sortMode},
        userLevels: {userLevel},
        routerHistory
    }) => (
        <div className="pending-route-h4y">
            <header>
                <a
                    className="start-new-order"
                    xd-qa-type="link"
                    xd-qa-name="Start a New Order"
                    onClick={() => routerHistory.push("/")}
                >
                    Start a New Order
                </a>
                <div className="search-input">
                    <input
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search"
                        title={searchInputTip}
                        value={search}
                        xd-qa-type="textbox"
                        xd-qa-name="Search"
                    />
                    <button onClick={() => setSearch("")}><FontAwesomeIcon icon={search ? faCircleXmark : faSearch}
                        xd-qa-type="button"
                        xd-qa-name="Search"
                    /></button>
                </div>
                <div className="group sort-group">
                    <Select2
                        list={sortModes}
                        getLabel={({label}) => `Sort by ${label}`}
                        isSelected={({id}) => id === sortMode.id}
                        onSelect={({id}) => setSortMode({id ,ascending: true})}
                        disableClearingSelection={true}

                        title="Choose a sorting method"
                        className="sort-select"
                    />
                    <button
                        onClick={() => changeSortMode(({ascending, id}) => ({id, ascending: !ascending}))}
                        className="sort-flip"
                    ><FontAwesomeIcon icon={sortMode.ascending ? faArrowUp : faArrowDown}/></button>
                </div>
            </header>

            {!ordersSubmittedState.loading && ordersSubmittedState.rows.length > 0 && (<>
                <section xd-qa-type="card" xd-qa-name="Submitted for Approval">
                    <header>
                        <h3 xd-qa-type="label" xd-qa-name={(userLevel === COMPANY_LEVEL) ? "Orders Submitted for Office Approval" : "Orders Submitted for Approval"}>
                            {(userLevel === COMPANY_LEVEL) ? "Orders Submitted for Office Approval" : "Orders Submitted for Approval"}
                        </h3>
                        <a className="action" onClick={() => setExpandedState({pending: pendingExpanded, submitted: !submittedExpanded})} xd-qa-type="link" xd-qa-name={submittedExpanded ? "Collapse" : "Expand"}>
                            {submittedExpanded ? "Collapse" : "Expand"}
                        </a>
                    </header>
                    {submittedExpanded && (
                        <div className="content"><OrdersSubmittedTable
                            {...ordersSubmittedState}
                            onRowDelete={ordersSubmittedState.handleRowDelete}
                            sortState={sortState}
                        /></div>
                    )}
                </section>

                {submittedExpanded && <hr/>}
            </>)}

            <section xd-qa-type="card" xd-qa-name="Pending Orders">
                <header>
                    <h3 xd-qa-type="label" xd-qa-name="Pending Orders">Pending Orders</h3>
                    {!pendingOrdersState.loading && pendingOrdersState.rows.length > 0 && (
                        <a className="action" onClick={() => setExpandedState({pending: !pendingExpanded, submitted: submittedExpanded})} xd-qa-type="link" xd-qa-name={pendingExpanded ? "Collapse" : "Expand"}>
                            {pendingExpanded ? "Collapse" : "Expand"}
                        </a>
                    )}
                </header>
                {pendingExpanded && <div className="content"><PendingOrdersTable {...pendingOrdersState} filterApplied={Boolean(search)} sortState={sortState}/></div>}
            </section>
        </div>
    ))

    .run()
;

